/**
 * @file cusa-content-tracking.js
 *
 * This script manages content click tracking and cart attribute synchronization.
 *
 * It relies on the global `window.cusaCartAttributes` object, which holds current cart attribute
 * values as seen from the frontend. This object is used to compare and register updates to the
 * `_cusa_content_clicks` and `_cusa_click_tracking_is_active` attributes, ensuring that localStorage
 * data and cart attributes remain in sync.
 */

/**
 * @typedef {Object<string, any>} CartAttributes
 * A loose representation of Shopify cart attributes.
 */

/**
 * @typedef {(attributes: CartAttributes, showLogs?: boolean) => void} RegisterCartAttributeUpdate
 *
 * Function signature for the shared cart update aggregator used to batch cart-attribute updates.
 */

/**
 * @constant
 * @type {boolean}
 * Master toggle for console logging in this module.
 */
const SHOW_LOGS = true;

SHOW_LOGS && console.log('window.cusaCartAttributes = ');
SHOW_LOGS && console.log(window.cusaCartAttributes);

/**
 * @function splitString
 * @param {string | null | undefined} string
 * @returns {string[]}
 *
 * Splits a delimited content-click string into an array.
 */
const splitString = (string) => {
	return string ? string.split(CUSA_CONTENT_CLICK_DELIMITER) : [];
};

/**
 * @function updateCusaContentClicksCartAttribute
 * @param {string} updatedCusaContentClickString
 * @returns {void}
 *
 * Updates the in-memory `window.cusaCartAttributes` proxy and registers a cart-attribute update
 * via the shared cart update aggregator (if present).
 */
const updateCusaContentClicksCartAttribute = (updatedCusaContentClickString) => {
	window.cusaCartAttributes = {
		...(window.cusaCartAttributes || {}),
		_cusa_content_clicks: updatedCusaContentClickString
	};

	/** @type {RegisterCartAttributeUpdate | undefined} */
	if (typeof registerCartAttributeUpdate === 'function') {
		registerCartAttributeUpdate(
			{
				_cusa_content_clicks: updatedCusaContentClickString
			},
			SHOW_LOGS
		);
	}
};

/**
 * @async
 * @function window.logCusaContentClickState
 * @returns {Promise<void>}
 *
 * Debug helper that logs the current content-click state across:
 * - Local storage (`cusa-content-clicks`)
 * - The window-level cart-attributes proxy (`window.cusaCartAttributes`)
 * - The live cart attributes retrieved from Shopify (`/cart.js`)
 */
window.logCusaContentClickState = async () => {
	const localStorageValue = getLocalStorage('cusa-content-clicks');
	const windowAttrs = window.cusaCartAttributes || null;
	let cartAttrs = null;

	try {
		const result = await fetch('/cart.js');
		const data = await result.json();
		cartAttrs = data.attributes || null;
	} catch (err) {
		console.error('[CUSA Content Tracking] Failed to load cart attributes:', err);
	}

	SHOW_LOGS && console.group('[CUSA Content Tracking] Content click state');
	SHOW_LOGS && console.log('LocalStorage cusa-content-clicks:', splitString(localStorageValue) || '—');
	SHOW_LOGS && console.log('Window cart attributes:', splitString(windowAttrs?._cusa_content_clicks) || '—');
	SHOW_LOGS && console.log('Cart attributes (/cart.js):', splitString(cartAttrs?._cusa_content_clicks) || '—');
	SHOW_LOGS && console.groupEnd();
};

/**
 *
 * This is a proxy for what's in the cart attributes on page load since it's populated with liquid at render.
 */
SHOW_LOGS && console.log('window cusa content clicks cart attributes:');

/**
 * @constant
 * @type {string[] | false}
 *
 * Split representation of the Liquid-rendered `_cusa_content_clicks` cart attribute.
 */
const splitCusaContentClicksCartAttribute =
	window.cusaCartAttributes?._cusa_content_clicks && splitString(window.cusaCartAttributes._cusa_content_clicks);

SHOW_LOGS && console.table(splitCusaContentClicksCartAttribute);

SHOW_LOGS && console.log('local storage cusa content click string:');

/**
 * @constant
 * @type {string[] | false}
 * Split representation of the local storage `cusa-content-clicks` value.
 */
const splitStoredCusaContentClickString =
	getLocalStorage('cusa-content-clicks') && splitString(getLocalStorage('cusa-content-clicks'));

SHOW_LOGS && console.table(splitStoredCusaContentClickString);

/**
 * @async
 * @function setCusaTrackingCartAttribute
 * @returns {Promise<void>}
 *
 * Registers the `_cusa_click_tracking_is_active` flag as a cart attribute
 * using the centralized cart update aggregator. This flag is used to determine
 * whether local storage content click values should be kept or cleared.
 */
const setCusaTrackingCartAttribute = async () => {
	registerCartAttributeUpdate(
		{
			_cusa_click_tracking_is_active: true
		},
		SHOW_LOGS
	);
	console.log('Tracking flag registered via aggregator.');
};

/**
 * @constant
 * @type {boolean}
 * True when the Liquid-rendered `window.cusaCartAttributes` object exists.
 */
const hasCartAttributes = window.cusaCartAttributes && typeof window.cusaCartAttributes === 'object';

if (!hasCartAttributes) {
	setCusaTrackingCartAttribute();
} else if (!window.cusaCartAttributes._cusa_click_tracking_is_active) {
	/**
	 * A missing tracking flag only means a fresh session when the cart is genuinely new.
	 * Third-party apps can replace the whole attribute set from a stale snapshot after an
	 * add to cart, which drops the flag while the cart still has items. Clearing local
	 * storage in that case would destroy real click data, so only clear on an empty cart.
	 */
	const cartIsGenuinelyFresh = window.cusaCartItemCount === 0;

	if (getLocalStorage('cusa-content-clicks')) {
		if (cartIsGenuinelyFresh) {
			console.log(
				'_cusa_click_tracking_is_active was not found in cart attributes and the cart is empty. Clearing any local storage for content clicks.'
			);
			window.localStorage.removeItem('cusa-content-clicks');
		} else {
			console.log(
				'_cusa_click_tracking_is_active was not found in cart attributes, but the cart has items. Keeping local click state; an app likely overwrote the attributes.'
			);
		}
	}

	setCusaTrackingCartAttribute();
}

/**
 * @async
 * @function compareStoredCusaContentClickVals
 * @returns {Promise<void>}
 *
 * Compares the stored local storage value (`cusa-content-clicks`) against the
 * current `_cusa_content_clicks` cart attribute. If local storage contains a longer
 * or newer value, it is re-registered for cart update via the aggregator.
 * This ensures the cart attribute remains in sync with tracked client-side interactions.
 */
const compareStoredCusaContentClickVals = async () => {
	const storedCusaContentClickString = getLocalStorage('cusa-content-clicks');
	const windowCusaContentClickCartAttribute = window.cusaCartAttributes?._cusa_content_clicks;

	const normalizedStored = normalizeCusaContentClicks(storedCusaContentClickString);

	if (!normalizedStored.contentString) {
		if (windowCusaContentClickCartAttribute) {
			updateCusaContentClicksCartAttribute('');
		}
		return;
	}

	if (storedCusaContentClickString !== normalizedStored.contentString) {
		setLocalStorage('cusa-content-clicks', normalizedStored.contentString);
	}
	if (windowCusaContentClickCartAttribute !== normalizedStored.contentString) {
		updateCusaContentClicksCartAttribute(normalizedStored.contentString);
		/**
		 * The page-load repair write can itself be reverted by a third-party overwrite a few
		 * seconds later (observed live on the test store), so it needs the same verify-and-retry
		 * pass an add to cart gets.
		 */
		handleCusaCartActivity();
	}
};

/**
 * @constant
 * @type {number}
 * How long to wait after cart activity before verifying the click attribute survived,
 * and between retry attempts. Third-party overwrites land within a few seconds of an add.
 */
const CUSA_CLICK_VERIFY_DELAY_MS = 2500;

/**
 * @constant
 * @type {number}
 * Maximum verify/rewrite attempts per burst of cart activity. Every attempt runs even
 * after clean reads, so a rewrite made on a late attempt still gets checked afterward.
 */
const CUSA_CLICK_VERIFY_MAX_ATTEMPTS = 4;

/**
 * @function cusaWait
 * @param {number} ms
 * @returns {Promise<void>}
 */
const cusaWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @function getExpectedCusaContentClicks
 * @returns {string}
 *
 * The normalized local storage click string — the source of truth the cart attribute
 * must match.
 */
const getExpectedCusaContentClicks = () => {
	return normalizeCusaContentClicks(getLocalStorage('cusa-content-clicks')).contentString;
};

/**
 * @async
 * @function verifyCusaContentClicksAfterCartActivity
 * @returns {Promise<void>}
 *
 * Verify-and-retry self-heal. After cart activity (an add to cart can trigger a third-party
 * app to replace all cart attributes from a stale snapshot), re-read the live cart a few
 * seconds later and re-register the local storage value if the click attribute was reverted.
 */
const verifyCusaContentClicksAfterCartActivity = async () => {
	for (let attempt = 1; attempt <= CUSA_CLICK_VERIFY_MAX_ATTEMPTS; attempt++) {
		await cusaWait(CUSA_CLICK_VERIFY_DELAY_MS);

		// Re-read each attempt — new clicks may have been recorded while we waited.
		const expected = getExpectedCusaContentClicks();
		if (!expected) return;

		let liveValue = null;
		try {
			const result = await fetch('/cart.js');
			const data = await result.json();
			liveValue = data.attributes?._cusa_content_clicks || '';
		} catch (err) {
			console.warn('[CUSA Content Tracking] Post-cart-activity verification read failed:', err);
			continue;
		}

		// A match here is not proof the danger has passed — the third-party overwrite can land
		// seconds AFTER a check that looked clean. Never exit early; keep checking every
		// attempt and rewrite only when a mismatch shows up.
		if (liveValue === expected) {
			continue;
		}

		SHOW_LOGS &&
			console.log(
				`[CUSA Content Tracking] Click attribute mismatch after cart activity (attempt ${attempt}). Re-registering from local storage.`
			);
		updateCusaContentClicksCartAttribute(expected);
	}
};

/**
 * @type {boolean}
 * True while a verification pass is running, so overlapping cart activity queues a single
 * follow-up pass instead of stacking concurrent polls.
 */
let cusaClickVerifyInFlight = false;

/**
 * @type {boolean}
 * True when cart activity arrived mid-verification and another pass is owed.
 */
let cusaClickVerifyQueued = false;

/**
 * @async
 * @function handleCusaCartActivity
 * @returns {Promise<void>}
 *
 * Entry point for cart-activity events. Coalesces bursts of activity into sequential
 * verification passes.
 */
const handleCusaCartActivity = async () => {
	if (cusaClickVerifyInFlight) {
		cusaClickVerifyQueued = true;
		return;
	}

	cusaClickVerifyInFlight = true;
	try {
		await verifyCusaContentClicksAfterCartActivity();
	} finally {
		cusaClickVerifyInFlight = false;
		if (cusaClickVerifyQueued) {
			cusaClickVerifyQueued = false;
			handleCusaCartActivity();
		}
	}
};

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
	subscribe(PUB_SUB_EVENTS.cartUpdate, handleCusaCartActivity);
	subscribe(PUB_SUB_EVENTS.cusaCartActivity, handleCusaCartActivity);
}

/**
 *
 * Fire on each page load because we need to make sure the two are always in sync.
 * Runs down here so the verify machinery above is defined when a repair write needs it.
 */
compareStoredCusaContentClickVals();

/**
 * @constant
 * @type {number}
 * Longest we hold a checkout click while flushing the click attribute to the cart.
 * Past this, navigation proceeds and the in-flight write is best effort.
 */
const CUSA_CHECKOUT_FLUSH_TIMEOUT_MS = 1500;

/**
 * @async
 * @function flushCusaContentClicksBeforeCheckout
 * @param {MouseEvent} event
 * @returns {Promise<void>}
 *
 * Flush-before-checkout. When a checkout button or link is clicked, hold navigation just
 * long enough to re-copy the local storage click value onto the cart, then continue.
 * This only writes to the cart — it never clears local storage — so backing out of
 * checkout leaves click state intact.
 */
const flushCusaContentClicksBeforeCheckout = async (event) => {
	const trigger =
		event.target instanceof Element &&
		event.target.closest('button[name="checkout"], a[href*="/checkout"], a.cart-drawer-top-checkout-link');
	if (!trigger) return;

	const expected = getExpectedCusaContentClicks();
	if (!expected) return;

	event.preventDefault();

	try {
		await Promise.race([
			safeCartUpdate({
				_cusa_content_clicks: expected,
				_cusa_click_tracking_is_active: true
			}),
			cusaWait(CUSA_CHECKOUT_FLUSH_TIMEOUT_MS)
		]);
	} catch (err) {
		console.warn('[CUSA Content Tracking] Pre-checkout click flush failed:', err);
	}

	if (trigger.tagName === 'A') {
		window.location.href = trigger.href;
	} else {
		// requestSubmit(trigger) keeps the checkout button in the form data and does not
		// re-fire this click handler.
		const form = trigger.form;
		if (form) {
			form.requestSubmit ? form.requestSubmit(trigger) : form.submit();
		}
	}
};

document.addEventListener('click', flushCusaContentClicksBeforeCheckout, true);
